import { memo, useCallback, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { Flex, Form, Input } from 'antd';

import { useViewerProfileData } from '@/entities/viewer';

import { valueOrNull } from '@/shared/lib/api';
import { useAntMessage, usePhoneValidation } from '@/shared/lib/hooks';
import { Button } from '@/shared/ui/button';

import { useUpdateProfileDataMutation } from '../../api/profile.api';
import type { ProfileData } from '../../model/types/profile';

import cls from './edit-profile-form.module.css';

type FormValues = ProfileData;

interface EditProfileFormProps {
    viewerId: number;
}

export const EditProfileForm = memo((props: EditProfileFormProps) => {
    const { viewerId } = props;

    const viewerData = useViewerProfileData();

    const { t } = useTranslation('profile-management');

    const [form] = Form.useForm<FormValues>();

    const message = useAntMessage();

    const phoneValidations = usePhoneValidation();

    const [updateProfileDataMutation, { isLoading, isSuccess }] =
        useUpdateProfileDataMutation();

    const initialValues = useMemo(() => {
        if (!viewerData) {
            return {};
        }

        const { id, is_social: isSocial, image, ...formValues } = viewerData;

        return formValues;
    }, [viewerData]);

    const onSave = useCallback(
        (formValues: FormValues) => {
            updateProfileDataMutation({
                id: viewerId,
                ...valueOrNull(formValues),
            });
        },
        [viewerId, updateProfileDataMutation],
    );

    useEffect(() => {
        if (isSuccess) {
            message.info?.({
                content: t('generalInfoSuccessMessage'),
            });
        }
    }, [message, isSuccess, t]);

    useEffect(() => {
        form.resetFields();
    }, [form, initialValues]);

    return (
        <Form
            autoComplete="off"
            className={cls.root}
            form={form}
            initialValues={initialValues}
            layout="vertical"
            onFinish={onSave}
        >
            <Form.Item
                label={t('generalFormUsernameInputLabel')}
                name="username"
            >
                <Input
                    autoComplete="off"
                    disabled={isLoading}
                    size="large"
                />
            </Form.Item>
            <Flex gap={16}>
                <Form.Item
                    className="full-width"
                    label={t('generalFormFirstNameInputLabel')}
                    name="first_name"
                >
                    <Input
                        autoComplete="off"
                        disabled={isLoading}
                        size="large"
                    />
                </Form.Item>
                <Form.Item
                    className="full-width"
                    label={t('generalFormLastNameInputLabel')}
                    name="last_name"
                >
                    <Input
                        autoComplete="off"
                        disabled={isLoading}
                        size="large"
                    />
                </Form.Item>
            </Flex>
            <Form.Item
                label={t('generalFormEmailInputLabel')}
                name="email"
            >
                <Input
                    autoComplete="off"
                    disabled
                    size="large"
                />
            </Form.Item>
            <Form.Item
                label={t('generalFormPhoneInputLabel')}
                name="phone"
                rules={phoneValidations}
            >
                <Input
                    disabled={isLoading}
                    size="large"
                />
            </Form.Item>
            <Form.Item
                className={cls.saveButton}
                shouldUpdate
            >
                {({ isFieldsTouched, getFieldsError }) => (
                    <Button
                        disabled={
                            !isFieldsTouched() ||
                            !!getFieldsError().filter(
                                ({ errors }) => errors.length,
                            ).length
                        }
                        htmlType="submit"
                        loading={isLoading}
                        title={t('generalFormButton')}
                    />
                )}
            </Form.Item>
        </Form>
    );
});

EditProfileForm.displayName = 'EditProfileForm';
